import {
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import BrandIcon from "./BrandIcon";
import { tokens } from "../theme";
import type { WidgetFeatures } from "../types/api";
import { DEFAULT_FEATURES } from "../lib/configHelpers";

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** From GET /v1/config.branding.show_powered_by (default true) */
  showPoweredBy?: boolean;
  /** From GET /v1/config.features — MVP keeps false flags hidden */
  features?: WidgetFeatures;
}

const styles: Record<string, CSSProperties> = {
  footer: {
    flexShrink: 0,
    padding: `${tokens.space[3]} ${tokens.space[4]} 14px`,
    background: tokens.surface.canvas,
  },
  fieldWrap: {
    position: "relative",
    flex: 1,
    display: "flex",
  },
  field: {
    flex: 1,
    width: "100%",
    minHeight: 100,
    maxHeight: 200,
    resize: "none",
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.lg,
    padding: "14px 16px",
    fontSize: tokens.font.size.md,
    fontFamily: "inherit",
    lineHeight: 1.4,
    color: tokens.text.primary,
    background: tokens.surface.input,
    outline: "none",
    boxSizing: "border-box" as const,
  },
  fieldWithSend: {
    paddingRight: 52,
  },
  sendBtn: {
    position: "absolute",
    right: 8,
    bottom: 8,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: tokens.gradient.accent,
    color: tokens.text.onBrand,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: tokens.shadow.send,
    padding: 0,
  },
  powered: {
    margin: "18px 0 0",
    textAlign: "center" as const,
    fontSize: tokens.font.size.xs,
    color: tokens.text.muted,
    fontWeight: tokens.font.weight.medium,
  },
};

export default function ChatInput({
  onSend,
  disabled,
  placeholder,
  showPoweredBy = true,
  features = DEFAULT_FEATURES,
}: ChatInputProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const canSend = value.trim().length > 0 && !disabled;
  const resolvedPlaceholder = placeholder ?? t("input.placeholder");

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <footer
      style={styles.footer}
      data-feature-voice={features.voice ? "on" : "off"}
      data-feature-upload={features.file_upload ? "on" : "off"}
      data-feature-pre-chat={features.pre_chat_form ? "on" : "off"}
    >
      <form onSubmit={handleSubmit}>
        <div style={styles.fieldWrap}>
          <textarea
            style={{
              ...styles.field,
              ...(canSend ? styles.fieldWithSend : null),
              opacity: disabled ? 0.6 : 1,
            }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            rows={1}
            aria-label={t("input.ariaLabel")}
          />
          {canSend && (
            <button
              type="submit"
              style={styles.sendBtn}
              aria-label={t("input.send")}
            >
              <BrandIcon
                name="sendWhite"
                size={18}
                style={{
                  position: "relative",
                  top: -1.5,
                  left: 1.5,
                }}
              />
            </button>
          )}
        </div>
      </form>
      {showPoweredBy && <p style={styles.powered}>{t("input.poweredBy")}</p>}
    </footer>
  );
}
